import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, TextField, FormControl, Select, MenuItem, IconButton, FormHelperText, Button} from '@mui/material'
import {usStates} from '../../../../utils/helpers'
import {Controller} from 'react-hook-form'
import {Add, Delete} from '@mui/icons-material'
import {useFieldArray} from 'react-hook-form'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useParams} from 'react-router-dom'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {parseError, phoneRegex} from '../../../../utils/helpers'
import {caregiverApi} from '../../../../services'
import {AddressAutocompleteField, SkeletonContainer, FieldSkeleton, ButtonSkeleton} from '../../../../components'

const ContactAndAddressSkeleton = () => (
    <SkeletonContainer>
        <div className="form" style={{marginBottom: 0, alignItems: 'flex-end'}}>
            <FieldSkeleton labelWidth={85} sx={{minWidth: 155}} />
            <FieldSkeleton label={false} sx={{flex: 1}} />
        </div>
        <br />
        <ButtonSkeleton width={170} />
        <br />
        <hr className="line" />
        <br />
        <Typography variant="overline" color="text.secondary">
            ADDRESS
        </Typography>
        <br />
        <FieldSkeleton labelWidth={125} />
        <br />
        <FieldSkeleton labelWidth={120} />
        <br />
        <div className="form" style={{marginBottom: 0}}>
            <FieldSkeleton labelWidth={35} sx={{flex: 1}} />
            <FieldSkeleton labelWidth={40} sx={{flex: 1}} />
        </div>
        <br />
        <FieldSkeleton labelWidth={60} />
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={130} />
        </div>
    </SkeletonContainer>
)

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const [contactId, setContactId] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                primaryPhoneNumberType: yup.string().default('mobile'),
                primaryPhoneNumber: yup
                    .string()
                    .required('Phone number is required')
                    .test('is-valid-phone', 'Invalid phone number', value => phoneRegex.test(value)),
                primaryPhoneNumbers: yup.array().of(
                    yup.object().shape({
                        type: yup.string().default('mobile'),
                        number: yup.string().test('phone-optional', 'Invalid phone number', val => {
                            if (!val || val.trim() === '') return true
                            return phoneRegex.test(val)
                        }),
                    }),
                ),
                streetAddressLine1: yup.string().required('Address Line 1 is required'),
                streetAddressLine2: yup.string(),
                city: yup.string().required('City is required'),
                state: yup.string().required('State is required'),
                zipCode: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
            }),
        ),
    })
    const {
        fields: primaryPhoneNumberFields,
        append: appendPrimaryPhoneNumber,
        remove: removePrimaryPhoneNumber,
    } = useFieldArray({control: form?.control, name: 'primaryPhoneNumbers'})
    const primaryPhoneNumberTypes = [
        {key: 'mobile', value: 'Mobile'},
        {key: 'work', value: 'Work'},
        {key: 'home', value: 'Home'},
    ]

    const selectAddress = selected => {
        form.setValue('streetAddressLine2', '')
        form.setValue('city', selected?.city)
        form.setValue('state', selected?.state)
        form.setValue('zipCode', selected?.postcode)
    }

    useEffect(() => {
        reset(true)
    }, [])

    // The first load shows the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        if (!initial) setLoading(true)
        caregiverApi
            .getCaregiverContacts(branchId, caregiverId)
            .then(({data}) => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const contact = data?.contacts?.[0]
                setContactId(contact?.id)
                form.setValue('primaryPhoneNumberType', contact?.phones?.find(item => item.is_primary)?.type)
                form.setValue('primaryPhoneNumber', contact?.phones?.find(item => item.is_primary)?.number)
                contact?.phones?.map(phone => {
                    if (!phone?.is_primary) {
                        appendPrimaryPhoneNumber({type: phone?.type, number: phone?.number})
                    }
                })
                form.setValue('streetAddressLine1', contact?.addresses?.[0]?.street_line1)
                form.setValue('streetAddressLine2', contact?.addresses?.[0]?.street_line2)
                form.setValue('city', contact?.addresses?.[0]?.city)
                form.setValue('state', contact?.addresses?.[0]?.state)
                form.setValue('zipCode', contact?.addresses?.[0]?.zip_code)
                form.setValue('isPrimary', contact?.addresses?.[0]?.is_primary)
            })
            .catch(error => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const submit = async data => {
        try {
            setLoading(true)

            let primaryPhoneNumbers = []
            data?.primaryPhoneNumbers?.map(item =>
                primaryPhoneNumbers.push({
                    type: item?.type,
                    number: item?.number,
                    is_primary: false,
                }),
            )
            await caregiverApi.updateCaregiverContact(branchId, caregiverId, contactId, {
                type: 'primary',
                phones: [
                    {
                        type: data?.primaryPhoneNumberType,
                        number: data?.primaryPhoneNumber,
                        is_primary: true,
                    },
                    ...primaryPhoneNumbers,
                ],
                addresses: [
                    {
                        type: 'home',
                        street_line1: data?.streetAddressLine1,
                        street_line2: data?.streetAddressLine2,
                        city: data?.city,
                        state: data?.state,
                        zip_code: data?.zipCode,
                        is_primary: true,
                    },
                ],
            })

            setMessageBox({
                status: 'success',
                title: 'Caregiver Informations Updated',
                description: "Caregiver's Contacts have been successfully updated. All changes are now reflected in the system.",
            })
            setLoading(false)
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {skeleton && <ContactAndAddressSkeleton />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Phone Number{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <div className="form">
                    <FormControl style={{minWidth: 155}}>
                        <Controller
                            {...form.register(`primaryPhoneNumberType`)}
                            control={form.control}
                            render={({field}) => (
                                <Select
                                    {...field}
                                    value={field.value ?? 'mobile'}
                                    onChange={e => field.onChange(e.target.value)}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}>
                                    {primaryPhoneNumberTypes.map(item => (
                                        <MenuItem key={item?.key} value={item?.key}>
                                            {item?.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                    <TextField
                        placeholder="Enter your phone number"
                        fullWidth
                        {...form.register('primaryPhoneNumber')}
                        error={!!form.formState.errors['primaryPhoneNumber']}
                        helperText={form.formState.errors['primaryPhoneNumber']?.message}
                    />
                </div>
                {primaryPhoneNumberFields.map((field, index) => (
                    <div key={field.id} style={{marginTop: -14}}>
                        <br />
                        <br />
                        <Typography variant="inputLabel" color="text.secondary">
                            Phone Number
                        </Typography>
                        <div className="form">
                            <FormControl style={{minWidth: 155}}>
                                <Controller
                                    {...form.register(`primaryPhoneNumbers.${index}.type`)}
                                    control={form.control}
                                    render={({field}) => (
                                        <Select
                                            {...field}
                                            value={field.value ?? 'mobile'}
                                            onChange={e => field.onChange(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                            }}>
                                            {primaryPhoneNumberTypes.map(item => (
                                                <MenuItem key={item?.key} value={item?.key}>
                                                    {item?.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                            <TextField
                                placeholder="Enter your phone number"
                                fullWidth
                                {...form.register(`primaryPhoneNumbers.${index}.number`)}
                                error={!!form.formState.errors?.primaryPhoneNumbers?.[index]?.number}
                                helperText={form.formState.errors?.primaryPhoneNumbers?.[index]?.number?.message}
                            />
                            <IconButton onClick={() => removePrimaryPhoneNumber(index)}>
                                <Delete color="error" />
                            </IconButton>
                        </div>
                    </div>
                ))}
                <br />
                <br />
                <Button variant="text" startIcon={<Add />} onClick={() => appendPrimaryPhoneNumber({value: ''})}>
                    Add Phone Number
                </Button>
                <br />
                <hr className="line" />
                <br />
                <Typography variant="overline" color="text.secondary">
                    ADDRESS
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 1{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <AddressAutocompleteField
                    control={form.control}
                    name="streetAddressLine1"
                    error={!!form.formState.errors['streetAddressLine1']}
                    helperText={form.formState.errors['streetAddressLine1']?.message}
                    onSelect={selectAddress}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 2
                </Typography>
                <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('streetAddressLine2')} />
                <br />
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            City{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your city"
                            fullWidth
                            {...form.register('city')}
                            error={!!form.formState.errors['city']}
                            helperText={form.formState.errors['city']?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            State{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <FormControl fullWidth error={!!form.formState.errors['state']}>
                            <Controller
                                {...form.register('state')}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                            anchorOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={selected => {
                                            if (selected === '') {
                                                return <span style={{color: '#aaa'}}>Select your state</span>
                                            }
                                            return selected
                                        }}>
                                        {usStates.map(item => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{form.formState.errors['state']?.message}</FormHelperText>
                        </FormControl>
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Zip Code{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your zip code"
                    fullWidth
                    {...form.register('zipCode')}
                    error={!!form.formState.errors['zipCode']}
                    helperText={form.formState.errors['zipCode']?.message}
                />
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
            </div>
        </>
    )
}
